import React, { useEffect, useState, useContext } from "react";
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import { BarChart } from '@mui/x-charts/BarChart';
import { ReviewsContext } from '../context/ReviewsContext';
import { BusinessContext } from '../context/BusinessContext';
import { formatCurrency } from '../utils/currencyUtils';
import { applySalesMultiplier } from '../utils/chartUtils';
import { formatChartValue, formatAxisValue } from '../utils/formatUtils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { 
  DashboardCardTitle, 
  CHART_MARGINS, 
  MOBILE_CHART_MARGINS, 
  MODERN_CARD_STYLE, 
  CARD_HEADER_STYLE, 
  CARD_CONTENT_STYLE,
  StandardChartCard,
  useResponsiveChartProps,
  getXAxisConfig,
  getYAxisConfig,
  CustomChartTooltip
} from './common/ChartDefaults';
import { useMediaQuery, useTheme } from '@mui/material';
import { colors } from '../utils/themeUtils';
import { Box } from '@mui/material';

/**
 * This component displays impact over time based on selected view (Sales or Rating)
 * It dynamically adjusts formatting, labels, and calculations based on the current view
 */
const ImpactOverTimeChart = () => {
  const [categories, setCategories] = useState([]);
  const [positiveData, setPositiveData] = useState([]);
  const [negativeData, setNegativeData] = useState([]);
  const { reviewsData, selectedCategory, selectedSubject, selectedAttribute, view } = useContext(ReviewsContext);
  const { currencySymbol, selectedBusiness } = useContext(BusinessContext);
  const { setSelectedDate } = useContext(ReviewsContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (reviewsData && selectedBusiness) {
      try {
        const impactsByMonth = {};

        // Generate full range of months
        if (reviewsData.length > 0) {
          const dates = reviewsData.map(review => new Date(review.estimated_datetime));
          const startDate = new Date(Math.min(...dates));
          const endDate = new Date(Math.max(...dates));

          for (let d = new Date(startDate); d <= endDate; d.setMonth(d.getMonth() + 1)) {
            const monthKey = d.toLocaleString('default', { month: 'short', year: 'numeric' });
            impactsByMonth[monthKey] = { positive: 0, negative: 0 }; // Initialize with zeros
          }
        }

        // Process reviews data and populate impactsByMonth
        reviewsData.forEach(review => {
          if (!review.analysis || review.analysis.length === 0) return;
          const date = new Date(review.estimated_datetime);
          const monthKey = date.toLocaleString('default', { month: 'short', year: 'numeric' });

          review.analysis.forEach(item => {
            if (
              (selectedCategory && item.category?.toLowerCase() !== selectedCategory.toLowerCase()) ||
              (selectedSubject && item.subject_group?.toLowerCase() !== selectedSubject.toLowerCase()) ||
              (selectedAttribute && item.attribute?.toLowerCase() !== selectedAttribute.toLowerCase())
            ) {
              return;
            }

            const { attribute_impact } = item;
            if (!impactsByMonth[monthKey]) {
              impactsByMonth[monthKey] = { positive: 0, negative: 0 }; // Safety fallback
            }
            if (attribute_impact > 0) {
              impactsByMonth[monthKey].positive += attribute_impact;
            } else if (attribute_impact < 0) {
              impactsByMonth[monthKey].negative += attribute_impact;
            }
          });
        });

        // Sort months and prepare data for the chart
        const sortedMonths = Object.keys(impactsByMonth).sort(
          (a, b) => new Date(a) - new Date(b)
        );

        const posData = sortedMonths.map(month => impactsByMonth[month].positive);
        const negData = sortedMonths.map(month => impactsByMonth[month].negative);

        // Apply sales multiplier for "Sales" view - for "Rating" view, use raw impact values
        if (view === "Sales") {
          const adjustedPositiveData = applySalesMultiplier(posData, "Sales", selectedBusiness.turnover);
          const adjustedNegativeData = applySalesMultiplier(negData, "Sales", selectedBusiness.turnover);
          setPositiveData(adjustedPositiveData);
          setNegativeData(adjustedNegativeData);
        } else {
          // For Rating view, use raw impact values
          setPositiveData(posData);
          setNegativeData(negData);
        }

        setCategories(sortedMonths);
      } catch (error) {
        console.error(error);
      }
    }
  }, [reviewsData, selectedBusiness, selectedCategory, selectedSubject, selectedAttribute, view]);

  // Handler for bar click; adjust based on your chart library's API:
  const onAxisClick = (event, params) => {
    // Assuming params includes dataIndex corresponding to the clicked bar
    if (params && params.dataIndex !== undefined) {
      // Retrieve the month label (or date) from your categories array using the index.
      const clickedDate = new Date(categories[params.dataIndex]);
      setSelectedDate(clickedDate);
      console.log(clickedDate)
    }
  };

  // Adjust title based on the view (Sales or Rating)
  let title = view === "Sales" ? "Revenue Impact Trends" : "Rating Impact Trends";

  if (selectedCategory || selectedSubject || selectedAttribute) {
    title += " for";
    if (selectedCategory) title += ` ${selectedCategory}`;
    if (selectedSubject) title += `${selectedCategory ? "," : ""} ${selectedSubject}`;
    if (selectedAttribute) title += `${(selectedCategory || selectedSubject) ? "," : ""} ${selectedAttribute}`;
  }

  const chartSx = { 
    cursor: 'pointer',
    height: '100%',
    width: '100%',
    flexGrow: 1,
    '.MuiChartsAxis-label': {
      fill: '#546E7A',
      fontWeight: 500,
      fontSize: isSmallScreen ? 10 : 12
    },
    '.MuiChartsAxis-tickLabel': {
      fill: '#546E7A',
      fontSize: isSmallScreen ? 9 : 11
    }
  };

  const slotProps = { 
    legend: { 
      hidden: true 
    },
    tooltip: {
      // Use our custom tooltip component with the appropriate view context
      content: (props) => <CustomChartTooltip {...props} currencySymbol={currencySymbol} view={view} />
    }
  };

  const margin = isSmallScreen ? MOBILE_CHART_MARGINS : CHART_MARGINS;

  return (
    <StandardChartCard 
      title={title} 
      titleIcon={<TrendingUpIcon />}
    >
      <BarChart
        sx={chartSx}
        onAxisClick={onAxisClick}
        xAxis={[getXAxisConfig(categories, isSmallScreen)]}
        yAxis={[getYAxisConfig(currencySymbol, view, isSmallScreen)]}
        series={[
          {
            label: 'Positive',
            data: positiveData,
            stack: 'impact',
            stackOffset: 'diverging',
            color: colors.positiveGreen,
            valueFormatter: (value) => formatChartValue(value, currencySymbol, view),
          },
          {
            label: 'Negative',
            data: negativeData,
            stack: 'impact',
            color: colors.negativeRed,
            valueFormatter: (value) => formatChartValue(value, currencySymbol, view),
          },
        ]}
        slotProps={slotProps}
        margin={margin}
      />
    </StandardChartCard>
  );
};

export default ImpactOverTimeChart;
